// client/src/pages/Settings.js

import React, { useEffect, useState } from "react";
import "./Settings.css";
import "./Dashboard.css"; // ✅ Core dashboard shell
import CphereHeaderLogo from "../components/CphereHeaderLogo";
import profileSettings from "../assets/profilesettings.jpg";
import PasswordChangeDialog from "../components/PasswordChangeDialog";
import usePasswordChange, { isPasswordChangeSessionCurrent } from "../hooks/usePasswordChange";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const PASSWORD_CHANGE_SUCCESS_KEY = "cpherePasswordChangeSuccess";
const PASSWORD_CHANGE_EXPIRED_KEY = "cpherePasswordChangeExpired";
const PASSWORD_CHANGE_PENDING_RELOAD = "pending-reload";
const getClientAuthToken = () => {
  try { return localStorage.getItem("clientAuthToken") || localStorage.getItem("authToken"); }
  catch { return null; }
};

const Settings = () => {
  const navigate = useNavigate();
  const { user, email: authEmail, role: authRole, logout } = useAuth();

  const [userInfo, setUserInfo] = useState(null);
  const [preferredName, setPreferredName] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileStatus, setProfileStatus] = useState({
    message: "",
    type: "",
  });

  // General page errors
  const [error, setError] = useState("");

  const endPasswordChangeSession = (changedToken, showSuccess) => {
    if (!isPasswordChangeSessionCurrent(getClientAuthToken, changedToken)) return;
    const noticeKey = showSuccess ? PASSWORD_CHANGE_SUCCESS_KEY : PASSWORD_CHANGE_EXPIRED_KEY;
    const otherNoticeKey = showSuccess ? PASSWORD_CHANGE_EXPIRED_KEY : PASSWORD_CHANGE_SUCCESS_KEY;
    try {
      sessionStorage.removeItem(otherNoticeKey);
      sessionStorage.setItem(noticeKey, PASSWORD_CHANGE_PENDING_RELOAD);
    } catch {}
    logout();
  };

  // Password popup state
  const passwordChange = usePasswordChange(
    getClientAuthToken,
    (changedToken) => endPasswordChangeSession(changedToken, true),
    (changedToken) => endPasswordChangeSession(changedToken, false)
  );
  const { showPopup, handleOpenPopup } = passwordChange;

  const BASE =
    (process.env.REACT_APP_BACKEND_URL || "http://localhost:5000").replace(
      /\/+$/,
      ""
    );

  const handleLogoClick = () => {
    navigate("/dashboard");
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token =
          localStorage.getItem("clientAuthToken") ||
          localStorage.getItem("authToken");
        const email =
          authEmail ||
          user?.email ||
          localStorage.getItem("userEmail") ||
          localStorage.getItem("email");

        if (!email) {
          setError("Email is required.");
          setLoading(false);
          return;
        }

        const response = await fetch(`${BASE}/api/auth/profile-with-advertisers`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ email }),
        });

        if (response.ok) {
          const data = await response.json();
          setUserInfo(data);
          setPreferredName(
            data?.preferredDisplayName ||
              data?.user?.preferredDisplayName ||
              ""
          );
        } else {
          let errMsg = "Failed to fetch profile data.";
          try {
            const errorData = await response.json();
            errMsg = errorData.error || errMsg;
          } catch (_) {
            // ignore JSON parse error
          }
          setError(errMsg);
        }
      } catch (err) {
        console.error("Fetch user data error:", err);
        setError("A network error occurred. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [authEmail, user, BASE]);

  const handlePreferredNameSave = async () => {
    try {
      setSavingProfile(true);
      setProfileStatus({ message: "", type: "" });

      const token =
        localStorage.getItem("clientAuthToken") ||
        localStorage.getItem("authToken");

      const response = await fetch(`${BASE}/api/auth/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          preferredDisplayName: preferredName,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setProfileStatus({
          message: data?.error || "Failed to save preferred name.",
          type: "error",
        });
        return;
      }

      const nextPreferredName = data?.preferredDisplayName || "";
      setPreferredName(nextPreferredName);
      setUserInfo((prev) =>
        prev
          ? {
              ...prev,
              preferredDisplayName: nextPreferredName,
              user: {
                ...(prev.user || {}),
                preferredDisplayName: nextPreferredName,
              },
            }
          : prev
      );

      try {
        if (nextPreferredName) {
          localStorage.setItem("preferredDisplayName", nextPreferredName);
        } else {
          localStorage.removeItem("preferredDisplayName");
        }
        if (userInfo?.email || authEmail || user?.email) {
          localStorage.setItem(
            "email",
            userInfo?.email || authEmail || user?.email || ""
          );
          localStorage.setItem(
            "userEmail",
            userInfo?.email || authEmail || user?.email || ""
          );
        }
      } catch {}

      setProfileStatus({
        message: "Eden naming preference saved.",
        type: "success",
      });
    } catch (err) {
      console.error("Save preferred name error:", err);
      setProfileStatus({
        message: "A network error occurred. Please try again.",
        type: "error",
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const normalizedRole = (userInfo?.role || userInfo?.account || "").toLowerCase();

  return (
    <div className="core-dashboard settings-page">
      <div className="core-dashboard-content settings-page-content cortex-page-intro-shell">
        <header className="settings-header">
          {/* Powered by – ProfileHub-matched layout */}
          <CphereHeaderLogo
            alt="Powered by"
            className="settings-header-logo"
            onClick={handleLogoClick}
          />

          {/* Settings hero pill + shimmer (Profile / Settings) */}
          <div className="settings-hero-container">
            <div className="settings-hero-pill">
              <span className="settings-hero-pill-label cphere-hero-pill-wordmark">
                Profile
              </span>
              <span className="settings-hero-pill-tag cphere-hero-pill-tag">
                Settings
              </span>
            </div>
            <div className="settings-hero-shimmer-bar" />
          </div>
        </header>

        <div className="settings-content">
          {loading && <p className="settings-message">Loading...</p>}
          {error && <p className="settings-message error">{error}</p>}

          {userInfo && (
            <div className="settings-info">
              {/* Neon panel with hero badge */}
              <div className="settings-hero-badge-wrapper">
                <img
                  src={profileSettings}
                  alt="Profile"
                  className="settings-hero-img"
                />
              </div>

              <p>
                <strong>Name:</strong> {userInfo.name || "N/A"}
              </p>
              <p>
                <strong>Email:</strong> {userInfo.email || "N/A"}
              </p>
              <p>
                <strong>Mobile:</strong> {userInfo.mobile || "N/A"}
              </p>
              <p>
                <strong>Role:</strong>{" "}
                {userInfo.role || authRole || user?.role || userInfo.account || "N/A"}
              </p>

              <div className="settings-preferred-name-block">
                <label
                  className="settings-preferred-name-label"
                  htmlFor="settings-preferred-name"
                >
                  What should Eden call you?
                </label>
                <input
                  id="settings-preferred-name"
                  className="settings-preferred-name-input"
                  type="text"
                  value={preferredName}
                  onChange={(e) => setPreferredName(e.target.value)}
                  placeholder="Preferred Name"
                  maxLength={60}
                />
                <p className="settings-preferred-name-help">
                  Eden will use this name first. If left blank, Eden falls back
                  to your account name.
                </p>
                {profileStatus.message ? (
                  <p className={`settings-inline-status ${profileStatus.type}`}>
                    {profileStatus.message}
                  </p>
                ) : null}
                <button
                  className="settings-save-preferred-name-button"
                  onClick={handlePreferredNameSave}
                  disabled={savingProfile}
                  type="button"
                >
                  {savingProfile ? "Saving..." : "Save Preferred Name"}
                </button>
              </div>

              <p>
                <strong>Advertisers:</strong>
              </p>
              <ul className="settings-advertisers-list">
                {normalizedRole === "client" ? (
                  Array.isArray(userInfo.advertiserObjects) &&
                  userInfo.advertiserObjects.length > 0 ? (
                    [...userInfo.advertiserObjects]
                      .sort((a, b) =>
                        (a.advertiser || "").localeCompare(b.advertiser || "")
                      )
                      .map((adv, idx) => (
                        <li className="settings-advertiser-chip" key={idx}>
                          {adv.advertiser}
                        </li>
                      ))
                  ) : (
                    <li className="settings-advertiser-chip">No advertisers assigned.</li>
                  )
                ) : (
                  <li className="settings-advertiser-chip">ALL Advertisers</li>
                )}
              </ul>

              <button
                className="settings-edit-password-button"
                onClick={handleOpenPopup}
                type="button"
              >
                Edit Password
              </button>
            </div>
          )}
        </div>
      </div>

      {showPopup && (
        <PasswordChangeDialog flow={passwordChange} onSignIn={() => { logout(); navigate("/login", { replace: true }); }} />
      )}
    </div>
  );
};

export default Settings;
